/**
 * Where the signed-in user is kept in the browser.
 *
 * Every country's frontend is served from the same origin (`/so`, `/af`, ...),
 * and storage is shared per origin, so a single "authUser" key let one
 * country's app pick up another country's session. The key is scoped to this
 * build's country and base path so each app only ever sees its own.
 */
const SCOPE = [
  process.env.REACT_APP_COUNTRY_CODE || "",
  process.env.PUBLIC_URL || "/",
]
  .join(":")
  .toLowerCase();

export const AUTH_STORAGE_KEY = `authUser:${SCOPE}`;
const LAST_VISITED_KEY = `lastVisitedURL:${SCOPE}`;

/** The unscoped key used before sessions were separated per country. */
const LEGACY_AUTH_STORAGE_KEY = "authUser";

export const readAuthUser = (): any => {
  try {
    return JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY) || "null");
  } catch (e) {
    return null;
  }
};

export const writeAuthUser = (authUser: any) => {
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authUser));
};

export const clearAuthUser = () => {
  localStorage.removeItem(AUTH_STORAGE_KEY);
};

export const getAuthToken = (): string => readAuthUser()?.token || "";

/**
 * Remembers the page to return to after signing in. Tied to the user so the
 * next person to sign in on this tab is not sent to the previous user's page.
 */
export const saveLastVisitedURL = (url: string, userId: any) => {
  sessionStorage.setItem(LAST_VISITED_KEY, JSON.stringify({ url, userId }));
};

export const takeLastVisitedURL = (userId: any): string | null => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(LAST_VISITED_KEY) || "null");
    return saved && userId && saved.userId === userId ? saved.url : null;
  } catch (e) {
    return null;
  }
};

export const clearLastVisitedURL = () => {
  sessionStorage.removeItem(LAST_VISITED_KEY);
};

/**
 * Drops the legacy shared session (it may belong to any country) and reloads
 * this tab whenever another tab signs in as someone else or signs out, so a
 * page never keeps showing one user while its requests go out as another.
 */
export const initAuthStorage = () => {
  localStorage.removeItem(LEGACY_AUTH_STORAGE_KEY);
  sessionStorage.removeItem("lastVisitedURL");

  window.addEventListener("storage", (event) => {
    if (event.key !== AUTH_STORAGE_KEY && event.key !== null) return;
    const tokenOf = (value: string | null) => {
      try {
        return JSON.parse(value || "null")?.token || "";
      } catch (e) {
        return "";
      }
    };
    // `key === null` means storage was cleared entirely.
    const before = event.key === null ? "cleared" : tokenOf(event.oldValue);
    const after = event.key === null ? "" : tokenOf(event.newValue);
    if (before !== after) {
      window.location.reload();
    }
  });
};
